import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/react-hooks";
import {
    FormLayout,
    HeaderActions,
    Heading,
    Input,
    LoadingProgress,
    Panel,
    Select,
    Switch,
    Text,
} from "@/ui";
import { Icon } from "@/components";
import Content from "../../base/layout/content/Content";
import useForm from "../../base/hooks/form/useForm";
import useValidator from "../../base/hooks/form/useValidator";
import useFormActions from "../../base/hooks/form/useFormActions";
import useMeta from "../../base/hooks/useMeta.js";
import routes from "../../config/routes/routes";
import {
    MUTATION_CREATE_SYSTEM_APP,
    MUTATION_UPDATE_SYSTEM_APP,
    QUERY_SYSTEM_APP_LIST,
    QUERY_GET_SYSTEM_APP,
} from "../../apollo/module-operations/app";
import { categoryType } from "../../config/constant";
import moment from "moment";
import styles from "../store/GeneralsForm.module.scss";

const SaveIcon = () => <Icon icon="save" />;

const fieldError = (validator, name) =>
    validator?.[name]?.isInvalid ? validator[name].message : undefined;

const toSelectOptions = (options = []) =>
    options
        .filter(option => option?.value != null && option.value !== "")
        .map(option => ({
            value: String(option.value),
            label: option.label ?? String(option.value),
        }));

const AppForm = () => {
    const { resourceId, isCreate, user } = useMeta();

    const [loading, setLoading] = useState(!isCreate);

    const { inputs, setInputs, inputChange, selectValue, selectChange } =
        useForm();

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "name",
                method: "isEmpty",
                validWhen: false,
                message: "This name is required.",
            },
            {
                field: "handler",
                method: "isEmpty",
                validWhen: false,
                message: "This handler is required.",
            },
            {
                field: "category",
                method: "isEmpty",
                validWhen: false,
                message: "This category is required.",
            },
        ],
        {
            ...inputs,
        },
    );

    useQuery(QUERY_GET_SYSTEM_APP, {
        variables: {
            account_id: user.accountId,
            store_id: user.storeId,
            app_id: resourceId,
        },
        skip: isCreate || user.loading,
        onCompleted: resp => {
            setInputs(resp.getApp);
            setLoading(false);
        },
    });

    const options = {
        variables: {
            input: {
                id: inputs.id ?? "",
                status: inputs.status,
                name: inputs.name,
                handler: inputs.handler,
                category: inputs.category,
                created_at: inputs.created_at ?? moment(),
                updated_at: inputs.created_at ?? moment(),
                type: "System",
            },
        },
    };

    const { createOrUpdate, loading: saving } = useFormActions(
        "id",
        "App",
        MUTATION_CREATE_SYSTEM_APP,
        MUTATION_UPDATE_SYSTEM_APP,
        [
            {
                query: QUERY_SYSTEM_APP_LIST,
            },
        ],
        options,
        routes.APP_DETAIL.route,
        isValid,
        isSubmited,
    );

    const categoryOptions = useMemo(() => toSelectOptions(categoryType), []);

    const selectedCategory =
        selectValue(inputs.category, categoryType)?.value ??
        (inputs.category ? String(inputs.category) : undefined);

    const headerActions = useMemo(
        () => [
            {
                id: "save",
                label: isCreate ? "Create" : "Update",
                icon: <SaveIcon />,
                loading: saving,
                onClick: () => createOrUpdate(),
            },
        ],
        [isCreate, saving, createOrUpdate],
    );

    return (
        <Content>
            <Panel
                className={styles.root}
                header={
                    <div className={styles.headerBar}>
                        <div className={styles.headerText}>
                            <Heading level={4}>Form</Heading>
                            <Text tone="secondary" variant="body-md">
                                Please enter all the require App information
                            </Text>
                        </div>
                        <HeaderActions
                            actions={headerActions}
                            aria-label="App form actions"
                        />
                    </div>
                }
                showHeaderDivider
            >
                {loading ? (
                    <div className={styles.loading}>
                        <LoadingProgress
                            indeterminate
                            indicator="circular"
                            size="sm"
                            title="Loading"
                        />
                    </div>
                ) : (
                    <FormLayout>
                        <FormLayout.Section title="Information">
                            <Switch
                                checked={Boolean(inputs.status)}
                                label="Active"
                                name="status"
                                onChange={inputChange}
                            />
                            <FormLayout.Grid columns={3}>
                                <Input
                                    error={fieldError(validator, "name")}
                                    fullWidth
                                    label="Name *"
                                    name="name"
                                    onChange={inputChange}
                                    placeholder="Name"
                                    value={inputs.name || ""}
                                />
                                <Input
                                    error={fieldError(validator, "handler")}
                                    fullWidth
                                    label="Handler *"
                                    name="handler"
                                    onChange={inputChange}
                                    placeholder="Handler"
                                    value={inputs.handler || ""}
                                />
                                <Select
                                    error={fieldError(validator, "category")}
                                    fullWidth
                                    label="Category"
                                    onValueChange={value =>
                                        selectChange({ value }, "category")
                                    }
                                    options={categoryOptions}
                                    value={
                                        selectedCategory != null
                                            ? String(selectedCategory)
                                            : undefined
                                    }
                                />
                            </FormLayout.Grid>
                        </FormLayout.Section>
                    </FormLayout>
                )}
            </Panel>
        </Content>
    );
};

export default AppForm;
